/**
 * RM-safe failure copy (TASK-652).
 *
 * The document-generation pipeline emits operational reason tokens
 * (`concurrent_generation`, `retry_loop_detected`, sandbox fingerprints, UUIDs).
 * Those are diagnostics for engineers — an RM-facing surface must state the
 * business impact and the safe next action instead. Raw diagnostics stay in
 * console/debug logging and operational tooling only.
 */

// Known generator reasons (backend/services/document-export/generator/errors.py)
// mapped to business-impact copy with a next action.
const REASON_COPY: Array<[RegExp, string]> = [
  [/concurrent_generation/i, 'Another generation for this document is still running — wait a moment, then retry.'],
  [/cost_limit_exceeded/i, 'The AI generation budget is exhausted — generation resumes when the limit resets.'],
  [/provider_unavailable/i, 'The document service is temporarily unavailable — retry in a few minutes.'],
  [/payload_too_large/i, 'This document is too large to generate — reduce the content and retry.'],
  [/cancelled/i, 'Generation was cancelled before it finished — retry to generate again.'],
  [
    /retry_loop_detected|sandbox_failure|banned_import|banned_attr|banned_name|dunder_escape|syntax|empty_response|missing_kielis_import|multiple_blocks|open_outside_workspace/i,
    'Generation hit a repeated internal error — retry once; if it fails again, escalate to support.',
  ],
];

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const HEX_FINGERPRINT_RE = /\b[0-9a-f]{12,}\b/gi;
// Raw technical shapes that must never reach an RM verbatim.
const TECHNICAL_RE = /traceback|exception|stack|errno|http\s*5\d\d|\b5\d\d\b|_error\b|\w+_\w+_\w+/i;

const GENERIC =
  'Generation failed — retry once; if it fails again, escalate to support with the time it happened.';

export function humanizeGenerationError(raw?: string | null): string {
  if (!raw) return GENERIC;
  for (const [pattern, copy] of REASON_COPY) {
    if (pattern.test(raw)) return copy;
  }
  // Unknown message: only pass it through if it reads like a sentence, never a
  // token/fingerprint/stack fragment.
  const cleaned = raw.replace(UUID_RE, '').replace(HEX_FINGERPRINT_RE, '').trim();
  if (!cleaned || TECHNICAL_RE.test(cleaned) || !/\s/.test(cleaned)) return GENERIC;
  return cleaned;
}

export interface FailedExportLike {
  id: string;
  export_type: string;
  error_message?: string | null;
}

export interface GroupedFailure<T extends FailedExportLike> {
  key: string;
  latest: T;
  count: number;
  message: string;
}

/**
 * Group repeated failed attempts of the same document/action into one row
 * (the audit showed the same failed deck listed once per attempt).
 * Items are expected newest-first (as the API returns them).
 */
export function groupFailedExports<T extends FailedExportLike>(items: T[]): GroupedFailure<T>[] {
  const groups = new Map<string, GroupedFailure<T>>();
  for (const item of items) {
    const message = humanizeGenerationError(item.error_message);
    const key = `${item.export_type}::${message}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      groups.set(key, { key, latest: item, count: 1, message });
    }
  }
  return [...groups.values()];
}
